import React, {HTMLProps, PropsWithChildren, ReactNode, useState, useEffect, useRef, useLayoutEffect, useMemo, useCallback} from 'react';
import Guid from "../../models/Guid";
import {id} from "date-fns/locale";
import {useCalendrierProps} from "./calendrier";
import {ISelectedCells, TypeCalendrier} from './calendrier-interfaces';
import {format} from "date-fns";

const CalendrierCell = ({children, className, idUnite, date, ligne, colonne, isToday, isWeekend, ...props}: PropsWithChildren<HTMLProps<any> & { idUnite?: Guid, date?: Date, ligne?: number, colonne?: number, isToday?: Boolean, isWeekend?: Boolean }>) => {
    const cellRef = useRef<any>(null);
    const {setCellUnitesDimensions, typeCalendrier, highlightedCell, dateCourrante, setSelectedCells, setSelectedCellsBox, removeInitialGroupCellState, selectedCell, setIsPopupReserverOpen, uniteHorsServiceDates, selectedTypeUnite} = useCalendrierProps()
    const isSelect = selectedCell.some(f => f.ligne === ligne && f.colonne === colonne)
    const isHorsService = uniteHorsServiceDates?.find(u => date && idUnite && u.idUnite.toString().toLowerCase() === idUnite.toString().toLowerCase() && date >= u.dateDebut && date <= u.dateFin);
    const [screenWidth, setScreenWidth] = useState(window.innerWidth);
    let resizeTimer: ReturnType<typeof setTimeout> | undefined;

    useEffect(() => {
        const handleResize = () => {
            clearTimeout(resizeTimer);
            resizeTimer = setTimeout(function () {
                setScreenWidth(window.innerWidth);
            }, 100);
        };
        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    useEffect(() => {
        if (!cellRef.current || !setCellUnitesDimensions || !idUnite || !date) return;
        const cellDate = `${selectedTypeUnite?.id}|${idUnite}|${format(date, typeCalendrier === TypeCalendrier.Jours ? "yyyyMMdd" : "yyyyMM")}`
        setCellUnitesDimensions(`${selectedTypeUnite?.id}|${idUnite}`, {
                [cellDate]: {
                    OffsetTop: cellRef.current.offsetTop || 0,
                    OffsetHeight: cellRef.current.offsetHeight || 0,
                    OffsetLeft: cellRef.current.offsetLeft || 0,
                    OffsetWidth: cellRef.current.offsetWidth || 0
                }
            }
        )
        // })
    }, [cellRef?.current, dateCourrante, idUnite, selectedTypeUnite?.id, date, typeCalendrier, screenWidth])

    const cellStyle = useMemo(() => (isSelect ? {
        ...props.style,
        backgroundColor: isHorsService ? "rgba(15,32,49,0.53)" : "rgba(160,181,234,0.53)",
        border: highlightedCell && highlightedCell.ligne === ligne && highlightedCell.colonne === colonne
            ? "1px dashed #43568687" : '',
        userSelect: "none",
        cursor: isHorsService ? "not-allowed" : "default"
    } : {
        ...props.style,
        backgroundColor: isHorsService ? "rgba(0,0,0,0.85)" :
            isToday ? "rgba(254,250,205,255)"
                : isWeekend
                    ? "rgb(241,241,241)"
                    : "initial",
        cursor: isHorsService ? "not-allowed" : "default"
    }) as React.CSSProperties, [props.style, isSelect, isToday, isWeekend, isHorsService, highlightedCell, ligne, colonne]);

    const onCellDoubleClick = useCallback(() => {
        if (!isHorsService) {
            setIsPopupReserverOpen(true)
        }
    }, [isHorsService, setIsPopupReserverOpen]);

    const onCellMouseEnter = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
        if ((e.ctrlKey || e.buttons === 1 || e.shiftKey) && idUnite && date) {
            setSelectedCellsBox({idUnite, dates: [{debut: date, fin: date}], ligne: ligne, colonne: colonne} as ISelectedCells, e)
        }
    }, [idUnite, date, ligne, colonne, setSelectedCellsBox]);

    const onCellMouseUp = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
        removeInitialGroupCellState();
    }, []);

    const onCellMouseDown = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
        if (e.button === 2) {
            const cell = selectedCell.find(f => f.ligne == ligne && f.colonne == colonne);
            if (cell)
                return
        }
        if (idUnite && date)
            setSelectedCells({idUnite, dates: [{debut: date, fin: date}], ligne: ligne, colonne: colonne} as ISelectedCells, e)
    }, [selectedCell, idUnite, date, ligne, colonne, setSelectedCells]);

    const cellTitle = useMemo(() => (isHorsService ? `Hors-service \n${isHorsService.description}` : ""), [isHorsService]);
    return (
        <div title={cellTitle} col-number={colonne} row-number={ligne} ref={cellRef} {...props} className={`${className || ''} cell`}
             style={cellStyle}
             onDoubleClick={onCellDoubleClick}
             onMouseEnter={onCellMouseEnter}
             onMouseUp={onCellMouseUp}
             onMouseDown={onCellMouseDown}
        >
            {children || <>&nbsp;</>}
        </div>
    );
};

export default CalendrierCell;